// @ts-nocheck — Phase 2 file, not active in MVP. Type errors suppressed intentionally.
import { Session } from '@inventory/core';
import { BasePouchDBAdapter } from './BasePouchDBAdapter';

interface SessionDocument extends PouchDocument {
  userId: string;
  token: string;
  refreshToken: string;
  expiresAt: string;
  lastActivity: string;
  deviceInfo: {
    id: string;
    name: string;
    platform: string;
    browser?: string;
  };
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export class SessionPouchDBAdapter extends BasePouchDBAdapter<Session> {
  constructor(dbName: string) {
    super(dbName, 'session');
  }

  protected async toDocument(session: Session): Promise<SessionDocument> {
    return {
      _id: session.id,
      type: 'session',
      userId: session.userId,
      token: session.token,
      refreshToken: session.refreshToken,
      expiresAt: session.expiresAt.toISOString(),
      lastActivity: session.lastActivity.toISOString(),
      deviceInfo: session.deviceInfo,
      active: session.active,
      createdAt: session.createdAt.toISOString(),
      updatedAt: session.updatedAt.toISOString()
    };
  }

  protected fromDocument(doc: SessionDocument): Session {
    return {
      id: doc._id,
      userId: doc.userId,
      token: doc.token,
      refreshToken: doc.refreshToken,
      expiresAt: new Date(doc.expiresAt),
      lastActivity: new Date(doc.lastActivity),
      deviceInfo: doc.deviceInfo,
      active: doc.active,
      createdAt: new Date(doc.createdAt),
      updatedAt: new Date(doc.updatedAt)
    };
  }

  async save(session: Session): Promise<void> {
    const doc = await this.toDocument(session);
    try {
      const existing = await this.db.get(doc._id);
      await this.db.put({ ...doc, _rev: (existing as any)._rev });
    } catch (error) {
      if ((error as any).status === 404) {
        await this.db.put(doc);
      } else {
        throw this.handleError(error as any);
      }
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const doc = await this.db.get(id);
      await this.db.remove(doc as any);
    } catch (error) {
      if ((error as any).status !== 404) throw this.handleError(error as any);
    }
  }

  async findByUserId(userId: string): Promise<Session[]> {
    return this.query({ userId });
  }

  async findByToken(token: string): Promise<Session | null> {
    const sessions = await this.query({ token });
    return sessions[0] ?? null;
  }

  async findByRefreshToken(refreshToken: string): Promise<Session | null> {
    const sessions = await this.query({ refreshToken });
    return sessions[0] ?? null;
  }

  async findActive(): Promise<Session[]> {
    return this.query({ active: true });
  }
}